/** Schedules decoded clips against one audio clock. Never trims authored pauses.
 * Keep at most the current + next sentence scheduled; Stop cancels both. */
export class ScheduledAudio {
  private end = 0;
  private stopped = false;
  private downloads = new AbortController();
  private sources = new Map<AudioBufferSourceNode, () => void>();
  readonly ready: Promise<void>;
  constructor(private context: AudioContext = new AudioContext()) {
    // Construct/resume synchronously in the Play gesture, before network awaits.
    this.ready = context.resume();
    void this.ready.catch(() => {});
  }
  get remainingSeconds() { return Math.max(0, this.end - this.context.currentTime); }
  get signal(){return this.downloads.signal;}
  silence(milliseconds: number): AudioBuffer {
    if (this.stopped) throw new Error("Playback stopped.");
    if (!Number.isInteger(milliseconds) || milliseconds <= 0 || milliseconds > 10000) throw new Error("Invalid pause duration.");
    // AudioBuffer is zero initialized: exact sample-count silence, no network or TTS.
    return this.context.createBuffer(1, Math.round(this.context.sampleRate * milliseconds / 1000), this.context.sampleRate);
  }
  async decode(url: string): Promise<AudioBuffer> {
    if (this.stopped) throw new Error("Playback stopped.");
    const response = await fetch(url, { signal: AbortSignal.any([this.downloads.signal, AbortSignal.timeout(30000)]) });
    if (!response.ok) throw new Error("Unable to read buffered audio.");
    const buffer = await this.context.decodeAudioData(await response.arrayBuffer());
    if (this.stopped) throw new Error("Playback stopped.");
    return buffer;
  }
  enqueue(buffer: AudioBuffer, onStart?: () => void, offsetSeconds=0) {
    if (this.stopped) throw new Error("Playback stopped.");
    if(!Number.isFinite(offsetSeconds)||offsetSeconds<0||offsetSeconds>=buffer.duration)throw new Error('Invalid stream continuation.');
    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.connect(this.context.destination);
    const start = Math.max(this.context.currentTime + 0.015, this.end);
    const gapMs = this.end ? Math.max(0, start - this.end) * 1000 : 0;
    this.end = start + buffer.duration-offsetSeconds;
    const timer = setTimeout(() => { if (!this.stopped) onStart?.(); },
      Math.max(0, start - this.context.currentTime) * 1000);
    const ended = new Promise<void>(resolve => {
      const finish = () => {
        clearTimeout(timer); this.sources.delete(source); source.disconnect(); resolve();
      };
      this.sources.set(source, finish);
      source.onended = finish;
    });
    if(offsetSeconds)source.start(start,offsetSeconds);else source.start(start);
    return { ended, gapMs, startDelayMs: (start - this.context.currentTime) * 1000 };
  }
  stop() {
    if (this.stopped) return;
    this.stopped = true;
    this.downloads.abort();
    for (const [source, finish] of this.sources) {
      source.onended = null;
      try { source.stop(); } catch { /* Already ended. */ }
      finish();
    }
    void this.context.close().catch(() => {});
  }
}
